import Phaser from "phaser";
import { DEPTH } from "../objects/depthLayers.js";
import { PALETTE_NUM } from "../themes/palette.js";

const PAD_X = 12;
const EDGE_PAD_Y = 3;
const DASH = 16;
const GAP = 8;
const CHEVRON_STEP = 44;
const CHEVRON_W = 8;
const CHEVRON_H = 6;
const ZONE_HEIGHT = 18;
const PROJECTION_DASH = 6;
const PROJECTION_GAP = 5;
const PULSE_ALPHA = 0.42;
const PULSE_MS = 420;

export interface BattleDeadlineLineLayout {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface BattleDeadlineLineState {
  readonly visible: boolean;
  readonly y: number;
  readonly threatBottomY?: number | null;
}

/**
 * 일반 전투 패배 데드존. 벽돌 bottom이 band 중심선에 닿으면 패배하므로,
 * 위험 상태에서만 게임뷰 내부에 붉은 장벽과 위험 적 projection으로 표시한다.
 */
export class BattleDeadlineLine {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private layout: BattleDeadlineLineLayout;
  private lastY = Number.NaN;
  private lastThreatBottomY: number | null = null;
  private isVisible = false;

  constructor(
    scene: Phaser.Scene,
    layout: BattleDeadlineLineLayout,
    mask: Phaser.Display.Masks.GeometryMask,
  ) {
    this.layout = layout;
    this.graphics = scene.add
      .graphics()
      .setDepth(DEPTH.battleDeadlineLine)
      .setVisible(false)
      .setActive(false)
      .setMask(mask);
  }

  setLayout(layout: BattleDeadlineLineLayout): void {
    this.layout = layout;
    if (this.isVisible) this.draw(this.lastY, this.lastThreatBottomY);
  }

  update(state: BattleDeadlineLineState): void {
    if (!state.visible) {
      this.setVisible(false);
      return;
    }
    const y = this.clampY(state.y);
    const threatBottomY = typeof state.threatBottomY === "number"
      ? this.clampY(state.threatBottomY)
      : null;
    const threatChanged =
      (threatBottomY === null) !== (this.lastThreatBottomY === null) ||
      (threatBottomY !== null &&
        this.lastThreatBottomY !== null &&
        Math.abs(threatBottomY - this.lastThreatBottomY) > 0.5);
    if (!this.isVisible || Math.abs(y - this.lastY) > 0.5 || threatChanged) {
      this.draw(y, threatBottomY);
    }
    this.setVisible(true);
  }

  setVisible(visible: boolean): void {
    if (this.isVisible === visible) return;
    this.isVisible = visible;
    this.graphics.setVisible(visible).setActive(visible);
    this.graphics.scene.tweens.killTweensOf(this.graphics);
    if (!visible) {
      this.graphics.clear().setAlpha(1);
      this.lastY = Number.NaN;
      this.lastThreatBottomY = null;
      return;
    }
    this.graphics.setAlpha(1);
    this.graphics.scene.tweens.add({
      targets: this.graphics,
      alpha: PULSE_ALPHA,
      duration: PULSE_MS,
      ease: "Sine.easeInOut",
      yoyo: true,
      repeat: -1,
    });
  }

  private clampY(y: number): number {
    const minY = this.layout.y + EDGE_PAD_Y;
    const maxY = this.layout.y + Math.max(0, this.layout.height - EDGE_PAD_Y);
    return Phaser.Math.Clamp(y, minY, Math.max(minY, maxY));
  }

  private draw(y: number, threatBottomY: number | null): void {
    this.lastY = y;
    this.lastThreatBottomY = threatBottomY;
    const left = this.layout.x + PAD_X;
    const right = this.layout.x + Math.max(PAD_X, this.layout.width - PAD_X);
    const g = this.graphics;
    g.clear();

    const zoneTop = Math.max(this.layout.y, y - ZONE_HEIGHT / 2);
    const zoneBottom = Math.min(this.layout.y + this.layout.height, y + ZONE_HEIGHT / 2);
    g.fillStyle(PALETTE_NUM.signalDanger, 0.16);
    g.fillRect(left, zoneTop, right - left, Math.max(1, zoneBottom - zoneTop));

    g.lineStyle(6, PALETTE_NUM.signalDanger, 0.22);
    g.beginPath();
    g.moveTo(left, y);
    g.lineTo(right, y);
    g.strokePath();

    g.lineStyle(2, PALETTE_NUM.signalDanger, 0.95);
    for (let x = left; x < right; x += DASH + GAP) {
      g.beginPath();
      g.moveTo(x, y);
      g.lineTo(Math.min(right, x + DASH), y);
      g.strokePath();
    }

    g.fillStyle(PALETTE_NUM.signalDanger, 0.84);
    g.fillRect(left - 2, y - 8, 3, 16);
    g.fillRect(right - 1, y - 8, 3, 16);
    for (let x = left + CHEVRON_STEP / 2; x < right - CHEVRON_W; x += CHEVRON_STEP) {
      g.fillTriangle(x, y - CHEVRON_H, x + CHEVRON_W, y, x, y + CHEVRON_H);
    }

    if (threatBottomY !== null && threatBottomY < y - EDGE_PAD_Y) {
      this.drawThreatProjection(left, right, threatBottomY, y);
    }
  }

  private drawThreatProjection(left: number, right: number, fromY: number, toY: number): void {
    const centerX = (left + right) / 2;
    const halfW = Math.min(42, Math.max(18, (right - left) * 0.16));
    const g = this.graphics;

    g.lineStyle(2, PALETTE_NUM.signalDanger, 0.7);
    for (let y = fromY; y < toY; y += PROJECTION_DASH + PROJECTION_GAP) {
      g.beginPath();
      g.moveTo(centerX, y);
      g.lineTo(centerX, Math.min(toY, y + PROJECTION_DASH));
      g.strokePath();
    }

    g.lineStyle(1, PALETTE_NUM.signalDanger, 0.34);
    g.beginPath();
    g.moveTo(centerX - halfW, fromY);
    g.lineTo(centerX, toY);
    g.lineTo(centerX + halfW, fromY);
    g.strokePath();
  }
}
